import React from 'react';
import { Printer, Download } from 'lucide-react';
import { usePdfRenderer } from '../../../hooks/usePdfRenderer';
import ConsultationSummaryFormPDF from '../../../pdf/ConsultationSummaryFormPDF';
import { PatientInfoCard } from '../SummaryConsultation/PatientInfoCard';
import { VitalSignsCard } from '../SummaryConsultation/VitalSignsCard';
import { InfoCard } from '../SummaryConsultation/InfoCard';
import { MedicalConditionsList } from '../SummaryConsultation/MedicalConditionsList';
import { EditableList } from '../SummaryConsultation/EditableList';
import FinalizationStep from '../steps/FinalizationStep';
import type { Medication } from '../../../types/medication';
import type { Analyse } from '../../../types/analyse';
import { useConsultationWizardState } from "../../../hooks/useConsultationWizardState";

interface ConsultationSummaryProps {
  onBack: () => void;
  onSave: () => void;
  loading: boolean;
}

export const ConsultationSummary: React.FC<ConsultationSummaryProps> = ({
  onBack,
  onSave,
  loading,
}) => {
  const {
    formData,
    analyses,
    medications,
    setEditAnalyseIndex,
    setEditMedicationIndex
  } = useConsultationWizardState();
  const { download, print } = usePdfRenderer();

  const handlePrint = () =>
    print(
      <ConsultationSummaryFormPDF formData={formData} medications={medications} analyses={analyses} />
    );

  const handleDownload = () =>
    download(
      <ConsultationSummaryFormPDF formData={formData} medications={medications} analyses={analyses} />,
      `recap_consultation_${`${formData.patientInfo.firstName} ${formData.patientInfo.lastName}`.replace(/[^a-zA-Z0-9]/g, '_')}_${Date.now()}.pdf`
    );

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <h2 className="text-xl sm:text-2xl font-bold text-blue-800 mb-2 flex items-center">
          <span className="inline-block mr-2">📝</span>
          Récapitulatif de la consultation
        </h2>
        <div className="flex gap-2 print:hidden">
          <button onClick={handlePrint} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2">
            <Printer size={16} /> Imprimer
          </button>
          <button onClick={handleDownload} className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2">
            <Download size={16} /> Télécharger PDF
          </button>
        </div>
      </div>

      {/* Informations de base */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PatientInfoCard patientInfo={formData.patientInfo} />

        <VitalSignsCard
          poids={formData.poids}
          taille={formData.taille}
          tensionArterielle={formData.tensionArterielle}
          rythmeCardiaque={formData.rythmeCardiaque}
          temperature={formData.temperature}
        />
      </div>

      {/* Conditions médicales et antécédents */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InfoCard
          title="Conditions médicales"
          icon="🩺"
          iconColor="text-yellow-500"
          borderColor="border-yellow-100"
        >
          <div className="text-gray-700 text-sm space-y-3">
            {/* Durée des symptômes */}
            <div>
              <span className="font-semibold">Durée des symptômes :</span>
              <div className="mt-1 text-gray-600">
                {formData.patientInfo.dureeSymptomes || "Non spécifiée"}
              </div>
            </div>
            
            {/* Allergies */}
            <div>
              <span className="font-semibold">Allergies connues :</span>
              <div className="mt-1 text-gray-600">
                {formData.patientInfo.allergies || "Aucune"}
              </div>
            </div>
            
            {/* Médicaments actuels */}
            <div>
              <span className="font-semibold">Médicaments Actuels :</span>
              <div className="mt-1 text-gray-600">
                {formData.medicaments || "Aucun"}
              </div>
            </div>
            
            {/* Conditions médicales existantes */}
            <MedicalConditionsList
              conditions={formData.conditionsMedicales}
              bgColor="bg-yellow-50"
              borderColor="border-yellow-200"
            />
          </div>
        </InfoCard>

        <InfoCard
          title="Antécédents familiaux"
          icon="👪"
          iconColor="text-green-500"
          borderColor="border-green-100"
        >
          <MedicalConditionsList
            conditions={formData.antecedentsFamiliaux}
            bgColor="bg-green-50"
            borderColor="border-green-200"
          />
        </InfoCard>
      </div>

      {/* Observations systèmes */}
      <InfoCard
        title="Observations systèmes"
        icon="🔬"
        iconColor="text-indigo-500"
        borderColor="border-indigo-100"
      >
        <MedicalConditionsList
          conditions={formData.observationsSystemes}
          bgColor="bg-indigo-50"
          borderColor="border-indigo-200"
        />
      </InfoCard>

      {/* Informations gynécologiques - affichées uniquement si présentes */}
      {formData.informationGynecologique && (
        <InfoCard
          title="Informations gynécologiques"
          icon="🩺"
          iconColor="text-pink-500"
          borderColor="border-pink-100"
        >
          <div className="text-gray-700 text-sm space-y-3">
            {/* Informations générales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {formData.informationGynecologique.dateDernierRegle && (
                <div><b>Date dernières règles :</b> {formData.informationGynecologique.dateDernierRegle}</div>
              )}
              {formData.informationGynecologique.cycleTousLesJours && (
                <div><b>Durée du cycle :</b> {formData.informationGynecologique.cycleTousLesJours} jours</div>
              )}
              {formData.informationGynecologique.nombreGrossesses && (
                <div><b>Nombre de grossesses :</b> {formData.informationGynecologique.nombreGrossesses}</div>
              )}
              {formData.informationGynecologique.nombreEnfants && (
                <div><b>Nombre d'enfants :</b> {formData.informationGynecologique.nombreEnfants}</div>
              )}
              {formData.informationGynecologique.nombreEnfantsVivants && (
                <div><b>Enfants vivants :</b> {formData.informationGynecologique.nombreEnfantsVivants}</div>
              )}
              {formData.informationGynecologique.nombreFausseCouches && (
                <div><b>Fausses couches :</b> {formData.informationGynecologique.nombreFausseCouches}</div>
              )}
              {formData.informationGynecologique.typeAccouchement && (
                <div><b>Type d'accouchement :</b> {formData.informationGynecologique.typeAccouchement}</div>
              )}
              {formData.informationGynecologique.methodeContraceptiveActuelle && (
                <div><b>Contraception actuelle :</b> {formData.informationGynecologique.methodeContraceptiveActuelle}</div>
              )}
              {formData.informationGynecologique.dernierFrottis && (
                <div><b>Dernier frottis :</b> {formData.informationGynecologique.dernierFrottis}</div>
              )}
              {formData.informationGynecologique.derniereMammographie && (
                <div><b>Dernière mammographie :</b> {formData.informationGynecologique.derniereMammographie}</div>
              )}
            </div>

            {/* Informations de grossesse actuelle */}
            {formData.informationGynecologique.grossesseActuelle && (
              <div className="mt-4 pt-3 border-t border-pink-200">
                <h4 className="font-semibold text-pink-700 mb-2">Suivi de grossesse</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {formData.informationGynecologique.grossesseActuelle.dateDebutGrossesse && (
                    <div><b>Début de grossesse :</b> {formData.informationGynecologique.grossesseActuelle.dateDebutGrossesse}</div>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.dateAccouchementPrevu && (
                    <div><b>Date prévue d'accouchement :</b> {formData.informationGynecologique.grossesseActuelle.dateAccouchementPrevu}</div>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.groupeSanguin && (
                    <div><b>Groupe sanguin :</b> {formData.informationGynecologique.grossesseActuelle.groupeSanguin} {formData.informationGynecologique.grossesseActuelle.rh}</div>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.nombreFoetus && (
                    <div><b>Nombre de foetus :</b> {formData.informationGynecologique.grossesseActuelle.nombreFoetus}</div>
                  )}
                  <div><b>Grossesse désirée :</b> {formData.informationGynecologique.grossesseActuelle.grossesseDesiree ? 'Oui' : 'Non'}</div>
                  <div><b>Grossesse multiple :</b> {formData.informationGynecologique.grossesseActuelle.grossesseMultiple ? 'Oui' : 'Non'}</div>
                  {formData.informationGynecologique.grossesseActuelle.diabeteGestationnel && (
                    <div className="text-orange-600"><b>Diabète gestationnel</b></div>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.hypertensionArterielle && (
                    <div className="text-orange-600"><b>Hypertension artérielle</b></div>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.preeclampsie && (
                    <div className="text-red-600"><b>Prééclampsie</b></div>
                  )}
                  {formData.informationGynecologique.grossesseActuelle.menaceAccouchementPremature && (
                    <div className="text-red-600"><b>Menace d'accouchement prématuré</b></div>
                  )}
                </div>
                {formData.informationGynecologique.grossesseActuelle.traitementEnCours && (
                  <div className="mt-2"><b>Traitement en cours :</b> {formData.informationGynecologique.grossesseActuelle.traitementEnCours}</div>
                )}
                {formData.informationGynecologique.grossesseActuelle.antecedentsObstetricaux && (
                  <div className="mt-2"><b>Antécédents obstétricaux :</b> {formData.informationGynecologique.grossesseActuelle.antecedentsObstetricaux}</div>
                )}
                {formData.informationGynecologique.grossesseActuelle.commentaires && (
                  <div className="mt-2"><b>Commentaires :</b> {formData.informationGynecologique.grossesseActuelle.commentaires}</div>
                )}
              </div>
            )}
          </div>
        </InfoCard>
      )}

      {/* Ordonnances et analyses */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InfoCard
          title="Ordonnance"
          icon="💊"
          iconColor="text-teal-500"
          borderColor="border-teal-100"
          onEdit={onBack}
        >
          <EditableList
            items={medications}
            renderItem={(med: Medication) => (
              <span>
                <strong>{med.name}</strong> - {med.dose} - {med.duration} jours
              </span>
            )}
            onEdit={setEditMedicationIndex}
            onReturnToForm={onBack}
            emptyMessage="Aucune ordonnance prescrite"
            bgColor="bg-teal-50"
            borderColor="border-teal-200"
            buttonColor="text-teal-600"
            buttonHoverColor="text-teal-800"
          />
        </InfoCard>

        <InfoCard
          title="Analyses demandées"
          icon="🧪"
          iconColor="text-indigo-500"
          borderColor="border-indigo-100"
          onEdit={onBack}
        >
          <EditableList
            items={analyses}
            renderItem={(analyse: Analyse) => <span>{analyse.label}</span>}
            onEdit={setEditAnalyseIndex}
            onReturnToForm={onBack}
            emptyMessage="Aucune analyse demandée"
            bgColor="bg-indigo-50"
            borderColor="border-indigo-200"
            buttonColor="text-indigo-600"
            buttonHoverColor="text-indigo-800"
          />
        </InfoCard>
      </div>

      {/* Consultation actuelle */}
      <InfoCard
        title="Consultation actuelle"
        icon="🗒️"
        iconColor="text-gray-500"
        borderColor="border-gray-100"
      >
        <div className="text-gray-700 text-sm grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><b>Date :</b> {formData.consultationActuelle.date}</div>
          <div><b>Observations :</b> {formData.consultationActuelle.observations || "Aucune"}</div>
          <div><b>Diagnostics :</b> {formData.consultationActuelle.diagnostics || "Aucun"}</div>
          <div><b>Recommandations :</b> {formData.consultationActuelle.recommandations || "Aucune"}</div>
          {/* <div className="md:col-span-2">
            <b>Traitements :</b> {formData.consultationActuelle.traitements || "Aucun"}
          </div> */}
        </div>
      </InfoCard>

      {/* Finalisation */}
      <FinalizationStep
        onSave={onSave}
        onBack={onBack}
        loading={loading}
      />
    </div>
  );
};